import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStoredConsent, updateConsent } from "@/lib/tracking";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!getStoredConsent() && !window.__PRERENDER__) setVisible(true);
    const reopen = () => {
      const stored = getStoredConsent();
      setAnalytics(stored?.analytics === true);
      setMarketing(stored?.marketing === true);
      setSettings(false);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  const choose = (choice) => {
    updateConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-consent" className={`fixed bottom-[calc(72px+env(safe-area-inset-bottom,0px))] md:bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-2xl bg-[#111C2C] border border-white/10 rounded-2xl p-3 md:p-5 shadow-2xl ${settings ? "" : "max-md:max-h-[110px]"}`}>
      {/* Mobiel (<768px): compacte variant, max ~110px hoog, vlak boven de vaste belbalk */}
      <p className="md:hidden text-xs text-gray-300 leading-snug">
        We gebruiken cookies om de site te verbeteren.{" "}
        <Link to="/privacyverklaring" data-testid="consent-privacy-link-mobile" className="underline underline-offset-2 hover:text-[#20C7C9] transition-colors">
          Privacyverklaring
        </Link>
      </p>
      <p className="hidden md:block text-sm text-gray-300 leading-relaxed">
        Wij gebruiken cookies om de website goed te laten werken en om te meten welke advertenties en pagina's mensen helpen. U kiest zelf wat u toestaat.{" "}
        <Link to="/privacyverklaring" data-testid="consent-privacy-link" className="inline-block py-3 -my-2 underline underline-offset-2 hover:text-[#20C7C9] transition-colors">
          Lees onze privacyverklaring
        </Link>
        .
      </p>

      {settings && (
        <div className="mt-4 flex flex-col gap-3 border-t border-white/10 pt-4" data-testid="consent-settings">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-white">Noodzakelijk</p>
              <p className="text-[13px] sm:text-xs text-gray-400">Voor het veilig werken van de website. Altijd aan.</p>
            </div>
            <span className="text-[13px] sm:text-xs font-bold text-gray-500 uppercase tracking-wide">Altijd aan</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-white">Analytisch</p>
              <p className="text-[13px] sm:text-xs text-gray-400">Helpt ons te begrijpen welke pagina's goed werken.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={analytics}
              onClick={() => setAnalytics(!analytics)}
              data-testid="consent-toggle-analytics"
              className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${analytics ? "bg-[#20C7C9]" : "bg-white/20"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${analytics ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-white">Marketing</p>
              <p className="text-[13px] sm:text-xs text-gray-400">Meet welke advertenties tot contact leiden.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={marketing}
              onClick={() => setMarketing(!marketing)}
              data-testid="consent-toggle-marketing"
              className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${marketing ? "bg-[#20C7C9]" : "bg-white/20"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${marketing ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>
      )}

      <div className="mt-2 md:mt-4 flex flex-row items-center gap-2 md:gap-3">
        {settings ? (
          <button
            onClick={() => choose({ analytics, marketing })}
            data-testid="consent-save-button"
            className="flex-1 bg-[#20C7C9] text-black hover:bg-[#18A8AA] rounded-full px-4 sm:px-6 py-2.5 sm:py-3 font-bold text-[13px] sm:text-sm transition-colors duration-300"
          >
            Keuze opslaan
          </button>
        ) : (
          <>
            <button
              onClick={() => choose({ analytics: true, marketing: true })}
              data-testid="consent-accept-button"
              className="flex-1 bg-[#20C7C9] text-black hover:bg-[#18A8AA] rounded-full px-2 md:px-6 h-10 md:h-auto md:py-3 font-bold text-xs md:text-sm transition-colors duration-300 flex items-center justify-center"
            >
              <span className="md:hidden">Accepteren</span>
              <span className="hidden md:inline">Alles accepteren</span>
            </button>
            <button
              onClick={() => choose({ analytics: false, marketing: false })}
              data-testid="consent-decline-button"
              className="flex-1 border border-white/20 text-white hover:bg-white/10 rounded-full px-2 md:px-6 h-10 md:h-auto md:py-3 font-bold text-xs md:text-sm transition-colors duration-300 flex items-center justify-center"
            >
              Alleen noodzakelijk
            </button>
            <button
              onClick={() => setSettings(true)}
              data-testid="consent-settings-link"
              className="md:hidden shrink-0 px-1 h-10 text-[11px] text-gray-300 underline underline-offset-2 hover:text-white transition-colors"
            >
              Instellingen
            </button>
            <button
              onClick={() => setSettings(true)}
              data-testid="consent-settings-button"
              className="hidden md:inline-flex flex-1 border border-white/20 text-white hover:bg-white/10 rounded-full px-6 py-3 font-bold text-sm transition-colors duration-300 items-center justify-center"
            >
              Instellingen
            </button>
          </>
        )}
      </div>
    </div>
  );
}
